/**
 * Sentry initialisation for the frontend.
 *
 * Gated on VITE_SENTRY_DSN — if it's empty the SDK is never loaded and
 * adds no bundle weight beyond this file's own bytes. Vite inlines the
 * env var at build time so DSN updates require a re-deploy.
 *
 * The DSN is safe to expose in client bundles (it's a public, write-
 * only key — anyone can send events to your project but can't read
 * them out).
 */
import * as Sentry from '@sentry/react';

const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim();
export const sentryEnabled = !!dsn;

if (sentryEnabled) {
  const release = (import.meta.env.VITE_SENTRY_RELEASE as string | undefined)?.trim() || undefined;
  Sentry.init({
    dsn,
    // Tag each event with the readable app version + short commit
    // (e.g. "1.2.0+abc1234"). VITE_SENTRY_RELEASE is set by the build step
    // (see vite.config.ts) and matches the source-map upload release.
    release,
    environment: (import.meta.env.MODE as string) ?? 'development',
    // 10% of pageloads + navigations get a full performance trace.
    tracesSampleRate: 0.1,
    // Capture the previous route on navigation so breadcrumbs make sense.
    integrations: [Sentry.browserTracingIntegration()],
    // Drop expected auth failures. A 401 just means the session cookie
    // expired — the axios interceptor (api/client.ts) already redirects
    // the user to /login, so it's not a bug, just noise in the dashboard.
    beforeSend(event, hint) {
      const status = (hint?.originalException as { response?: { status?: number } })?.response?.status;
      if (status === 401) return null;
      return event;
    },
  });
  // Visible in the production browser console so you can confirm the DSN
  // got inlined into the bundle. Match the backend log line so the two
  // halves are easy to grep for.
  // eslint-disable-next-line no-console
  console.log('[sentry] enabled', { release: release ?? '(no release tag)' });
} else {
  // eslint-disable-next-line no-console
  console.log('[sentry] disabled — VITE_SENTRY_DSN missing from build');
}

export { Sentry };
